import type { ConsoleCapability } from "@flexfreight/core";
import { api, ApiError, setCsrfToken } from "../api/client";

export type ConsoleMe = {
  user: { id: string; email: string; name: string };
  role: string;
  capabilities: ConsoleCapability[];
  mfaEnabled: boolean;
  /** Customer portal origin, for screens still in the portal's operations area. */
  portalOrigin?: string | null;
  /** Where carrier calls go overall (top-bar badge). */
  carriers?: "mock" | "test" | "live" | "mixed" | "unknown";
};

export type MfaMethod = { method: "totp" | "email" | "sms"; available: boolean; hint: string | null };
type AuthMe = { user: { id: string; email: string; name: string } | null; mfaRequired: boolean; csrfToken: string; mfaMethods?: MfaMethod[]; mfaHelp?: string };

/** What the console gate should show. Server decides every state; nothing here is trusted for access. */
export type ConsoleSession =
  | { state: "signed_out" }
  | { state: "mfa_pending"; methods: MfaMethod[]; help: string | null }
  | { state: "staff_only"; email: string }
  | { state: "mfa_required"; email: string }
  | { state: "ready"; me: ConsoleMe };

export async function loadConsoleSession(): Promise<ConsoleSession> {
  let auth: AuthMe;
  try {
    auth = await api<AuthMe>("/v1/auth/me");
  } catch (e) {
    if (e instanceof ApiError && e.status === 401) return { state: "signed_out" };
    throw e;
  }
  setCsrfToken(auth.csrfToken);
  if (auth.mfaRequired) return { state: "mfa_pending", methods: auth.mfaMethods ?? [{ method: "totp", available: true, hint: null }], help: auth.mfaHelp ?? null };
  try {
    return { state: "ready", me: await api<ConsoleMe>("/v1/console/me") };
  } catch (e) {
    if (e instanceof ApiError && e.status === 401) return { state: "signed_out" };
    if (e instanceof ApiError && e.status === 403) {
      const email = auth.user?.email ?? "";
      return e.reason === "mfa_required" ? { state: "mfa_required", email } : { state: "staff_only", email };
    }
    throw e;
  }
}

export async function signIn(email: string, password: string): Promise<{ mfaRequired: boolean }> {
  const r = await api<AuthMe>("/v1/auth/login", { method: "POST", body: { email, password } });
  setCsrfToken(r.csrfToken);
  return { mfaRequired: r.mfaRequired };
}

/** Sign in with a code instead of a password (staff only). The server decides what the sign-in page may offer. */
export type SigninOptions = { email: boolean; sms: boolean };
export const signinByCode = {
  options: () => api<SigninOptions>("/v1/auth/signin/options"),
  /** Always the same generic answer, whether or not the address belongs to an account. */
  send: (email: string, method: "email" | "sms") => api<{ ok: true; method: "email" | "sms"; message: string; expiresInSeconds: number; resendAfterSeconds: number }>("/v1/auth/signin/code/send", { method: "POST", body: { email, method } }),
  verify: async (email: string, code: string): Promise<{ mfaRequired: boolean }> => {
    const r = await api<AuthMe>("/v1/auth/signin/code/verify", { method: "POST", body: { email, code } });
    setCsrfToken(r.csrfToken);
    return { mfaRequired: r.mfaRequired };
  },
};

/** Staff password reset / invite links (admin-console origin). The forgot answer is the same for any address. */
export const passwordApi = {
  forgot: (email: string) => api<{ ok: true }>("/v1/auth/password/forgot", { method: "POST", body: { email } }),
  lookup: (token: string) => api<{ email: string; name: string; invite: boolean; staff: boolean; expiresAt: string }>("/v1/auth/password/reset/lookup", { method: "POST", body: { token } }),
  reset: (token: string, password: string) => api<void>("/v1/auth/password/reset", { method: "POST", body: { token, password } }),
};

export async function verifyCode(code: string): Promise<void> {
  const r = await api<AuthMe>("/v1/auth/login/totp", { method: "POST", body: { code } });
  setCsrfToken(r.csrfToken);
}

/** Ask for a sign-in code by email or text (MFA-pending session). */
export async function sendLoginCode(method: "email" | "sms"): Promise<{ hint: string }> {
  return api<{ hint: string }>("/v1/auth/login/code/send", { method: "POST", body: { method } });
}

export async function verifyLoginCode(code: string): Promise<void> {
  const r = await api<AuthMe>("/v1/auth/login/code", { method: "POST", body: { code } });
  setCsrfToken(r.csrfToken);
}

export type MfaStatus = { totp: { enabled: boolean }; email: { enabled: boolean; available: boolean; hint: string }; sms: { enabled: boolean; available: boolean; hint: string | null } };
export const mfaApi = {
  status: () => api<MfaStatus>("/v1/auth/mfa"),
  startSms: (phone: string, password: string) => api<{ hint: string }>("/v1/auth/mfa/sms/start", { method: "POST", body: { phone, password } }),
  enable: (channel: "email" | "sms", code: string) => api<void>(`/v1/auth/mfa/${channel}/enable`, { method: "POST", body: { code } }),
};

export async function signOut(): Promise<void> {
  try {
    await api("/v1/auth/logout", { method: "POST" });
  } finally {
    setCsrfToken(null);
  }
}
